/*
 * Proprietary and Confidential
 *
 * Copyright (c) [2018] -  [] Marcelo H. Krebber - European Union 2018
 * All Rights Reserved.
 *
 * Dissemination or reproduction of this file [sidebar.js] or parts within
 * via any medium is strictly forbidden unless prior written permission is obtained
 * from <marcelo.krebber@gmail.com>
 *
 * Last modified: 26.09.18 20:49
 */

import {useDispatch} from 'react-redux';
import React, {Fragment, useEffect} from 'react';

import {asyncReorderSpaceRanking, GENERIC_SPACE,} from "../../actions/spaces";
import Widget from '../widgets/widget';
import {useAuthorization, useSiteConfiguration} from "../../selectors";
import {useLanguage} from "../configuration/configuration";
import SortableContainer, {SortableEntry} from "../sortable/sortable-container";
import {FETCH_ALL_SPACES, FETCH_SPACES_ONLY} from "../hooks/async-fetch-generic-spaces";
import {notify} from "../notifications/notify";
import {SUCCESS_STATUS} from "../../actions";
import {Spinner} from "../util/spinner";
import {ErrorBoundary} from "../error-boundary/ErrorBoundary";
import usePeekGenericSpaces from "../hooks/use-peek-generic-spaces";
import usePeekWidgets from "../hooks/use-peek-widgets";
import {getPublicUser} from "../../actions/environment";
import SidebarEntryPeekSpace from "./lists/sidebar-entry-peekspace";
import CalendarSliderTodayVertical from "../../views/calendar-view/calendar-slider-today-vertical";
import {NavigationGroup, NavigationRow} from "../navigation/layout/nav-layout";
import {RoundedButton} from "../navigation/buttons/nav-buttons";
import {fs_regular, GalleryIcon} from "../navigation/icons/nav-icons";
import {useHistory} from "react-router-dom";

export const SidebarNavigationHeadline = ({title, tooltip, callback}) => {
    return <NavigationRow className='bg-white rounded'>
        <NavigationGroup>
            <span className='sidebar-space-title'>{title}</span>
        </NavigationGroup>
        <NavigationGroup>
            <RoundedButton title={tooltip} onClick={callback}>
                <GalleryIcon size={fs_regular}/>
            </RoundedButton>
        </NavigationGroup>
    </NavigationRow>
}

const Sidebar = ({username, location, fetch = FETCH_ALL_SPACES}) => {
    const {superuser, authorization, isTransitioning, principalUsername, isAuthorized} = useAuthorization();
    const {homePage, siteUsername} = useSiteConfiguration();
    const {peekFeaturedSpaces, spaces, status: spacesStatus} = usePeekGenericSpaces();
    const {peekWidgets, widgets, status: widgetsStatus} = usePeekWidgets();
    const dispatch = useDispatch();
    const history = useHistory();
    const Lang = useLanguage();

    useEffect(() => {
        const {fetchname, isParent} = resolveUsernameByLocation(location);
        isRenderSpaces && peekFeaturedSpaces(fetchname);
    }, [username, location]);

    const resolveUsernameByLocation = (location) => {
        if (isAuthorized) {
            // superuser
            if (location.pathname === homePage) {
                return {fetchname: siteUsername, isParent: true}

                // logged-in user home space
            } else if (location.pathname === `/${principalUsername}/home`) {
                return {fetchname: principalUsername, isParent: false}

                // smthg.else
            } else return {fetchname: siteUsername, isParent: true}
        }
        return {fetchname: getPublicUser(), isParent: true}
    }

    const renderGenericSpaces = () => {
        if (!spaces || !Array.isArray(spaces)) {
            console.log('Spaces is null or is not an array. System error.', spaces);
            return null;
        }
        try {
            const genericSpaces = spaces
            .filter(space => space.type === GENERIC_SPACE)
            .map(space => {
                return <SortableEntry list key={space.id} className='list-unstyled mb-1'
                                      position={space.ranking}
                                      data={space.id}>
                    <SidebarEntryPeekSpace peekspace={space} size='medium'/>
                </SortableEntry>
            })
            return genericSpaces.length > 0 ? genericSpaces : null;

        } catch (error) {
            console.error('Sidebar', error, spaces);
            return <div className='mb-2'>{Lang.nav.sidebar.spaces.noneUpcoming} (Error)</div>;
        }
    }

    const renderTopWidgets = () => {
        if (!widgets) return null;
        return widgets
            .filter(widget => widget.pos === 'RTOP')
            .map(widget => {
                return <Widget key={widget.id} widget={widget} authname={principalUsername}
                           authorization={authorization} mode='RIGHT'/>
        })
    }

    const renderBottomWidgets = () => {
        if (!widgets) return '';
        return widgets.filter(widget => widget.pos === 'RBOTTOM').map(widget => {
            return <Widget key={widget.id} widget={widget} authname={principalUsername}
                           authorization={authorization} mode='RIGHT'/>
        })
    }

    // @Deprecated - reconsider functionality
    const reorderRanking = (evt) => {
        const ranks = [];
        const maxRanking = evt.to.children.length;
        [...evt.to.children].forEach((child, idx) => {
            child.dataset.space && ranks.push({spaceId: child.dataset.space, ranking: maxRanking - idx});
        });

        dispatch(asyncReorderSpaceRanking(principalUsername, {ranks: ranks}, spaces => {
                notify(`${spaces.length} spaces ranking set successfully`);
            })
        );
    };

    const handleOnShowKalendar = event => {
        event.preventDefault();
        history.push(`/${username}/calendar?type=events`);
    }

    const handleOnShowLocations = event => {
        event.preventDefault();
        history.push(`/${username}/calendar?type=spaces`);
    }

    const renderFallBack = () => {
        return <div className='mb-2'>
            {Lang.nav.sidebar.spaces.noneUpcoming}
            <p>(Something went wrong)</p>
        </div>;
    }

    const isRenderWidgets = false;
    const isRenderSpaces = fetch === FETCH_ALL_SPACES || fetch === FETCH_SPACES_ONLY;
    const isPending =
        (isRenderSpaces && spacesStatus !== SUCCESS_STATUS) ||
        (isRenderWidgets && widgetsStatus !== SUCCESS_STATUS);

    if (isPending || isTransitioning) return <Spinner/>;

    return <ErrorBoundary fallback={renderFallBack}>
        <div className="sidebar-container">
            {isRenderWidgets && <>
                <div className="widget-container">
                    {renderTopWidgets()}
                </div>
            </>}
        </div>

        <div className="sidebar-container">
            <SidebarNavigationHeadline title={Lang.nav.calendarSidebar.title.events}
                                       tooltip={Lang.nav.calendarSidebar.tooltip.events}
                                       callback={handleOnShowKalendar}/>
            <CalendarSliderTodayVertical/>
        </div>

        <div className='sidebar-container'>
            <SidebarNavigationHeadline title={Lang.nav.calendarSidebar.title.spaces}
                                       tooltip={Lang.nav.calendarSidebar.tooltip.spaces}
                                       callback={handleOnShowLocations}/>
            {isRenderSpaces && <>
                {spaces && <SortableContainer list className='list-group' onSorted={reorderRanking}>
                    {renderGenericSpaces()}
                </SortableContainer>}
            </>}
        </div>

        <div className="sidebar-container">
            {isRenderWidgets && <>
                <div className="widget-container pt-4">
                    {renderBottomWidgets()}
                </div>
            </>}
        </div>
    </ErrorBoundary>
}

export default Sidebar;